import React, { useState, useEffect, useCallback, useRef } from 'react';
import productoService from '../services/productoService';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useDialog } from '../contexts/DialogContext';
import LoadingSpinner from '../components/LoadingSpinner';
import './Productos.css';

// Componente de select con búsqueda
const SearchableSelect = ({ value, onChange, options, placeholder, disabled, allowCustom }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
        // Si allowCustom está activado y hay texto escrito, usarlo como valor
        if (allowCustom && searchTerm) {
          onChange(searchTerm.toUpperCase());
        }
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [searchTerm, allowCustom, onChange]);

  const filteredOptions = options.filter(option =>
    String(option).toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelect = (option) => {
    onChange(option);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && allowCustom) {
      e.preventDefault();
      const valueToUse = searchTerm || filteredOptions[0];
      if (valueToUse) {
        onChange(typeof valueToUse === 'string' ? valueToUse.toUpperCase() : valueToUse);
        setIsOpen(false);
        setSearchTerm('');
      }
    }
  };

  const displayValue = value || placeholder;

  return (
    <div className="searchable-select" ref={wrapperRef}>
      <div 
        className={`searchable-select-input ${disabled ? 'disabled' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        {isOpen ? (
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Buscar o escribir..."
            autoFocus
            disabled={disabled}
          />
        ) : (
          <span className={!value ? 'placeholder' : ''}>{displayValue}</span>
        )}
        <span className="searchable-select-arrow">▼</span>
      </div>
      {isOpen && !disabled && (
        <div className="searchable-select-dropdown">
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => (
              <div
                key={index}
                className={`searchable-select-option ${option === value ? 'selected' : ''}`}
                onClick={() => handleSelect(option)}
              >
                {option || 'Todos'}
              </div>
            ))
          ) : allowCustom && searchTerm ? (
            <div 
              className="searchable-select-option custom"
              onClick={() => handleSelect(searchTerm.toUpperCase())}
            >
              ✨ Usar "{searchTerm.toUpperCase()}"
            </div>
          ) : (
            <div className="searchable-select-option disabled">No se encontraron resultados</div>
          )}
        </div>
      )}
    </div>
  );
};

const Productos = () => {
  const { user } = useAuth();
  const { showConfirm } = useDialog();
  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Categoría seleccionada (tabs)
  const [categoriaSeleccionada, setCategoriaSeleccionada] = useState('MUEBLES');
  
  // Modal de crear producto
  const [modalAbierto, setModalAbierto] = useState(false);
  const [catalogo, setCatalogo] = useState([]);
  const [creando, setCreando] = useState(false);
  
  // Modal de editar producto
  const [modalEditar, setModalEditar] = useState(false);
  const [productoEditando, setProductoEditando] = useState(null);
  const [stockEditar, setStockEditar] = useState(0);
  const [sucursalEditar, setSucursalEditar] = useState(''); // Para que admin elija sucursal
  
  // Formulario de nuevo producto
  const [nuevoProducto, setNuevoProducto] = useState({
    categoria: '',
    tipo: '',
    modelo: '',
    medidas: '',
    madera: '',
    tapizado: '',
    cantidadTapizado: '',
    lustre: '',
    stock: 0,
    sucursal: '', // Para que el admin elija la sucursal
    // Campos específicos para DECORACION
    sku: '',
    proveedor: '',
    costo: 0
  });
  
  // Filtros
  const [filtros, setFiltros] = useState({
    tipo: '',
    modelo: '',
    medidas: '',
    madera: '',
    tapizado: '',
    lustre: '',
    cantidadTapizado: '',
    sucursal: user?.rol === 'admin' ? '' : (user?.sucursal?.toUpperCase() || ''), // Por defecto: admin ve todas, usuarios ven su sucursal
    sku: '',
    proveedor: ''
  });

  // Paginación
  const [paginaActual, setPaginaActual] = useState(1);
  const [itemsPorPagina, setItemsPorPagina] = useState(10);

  // Notificaciones no invasivas
  const [notificacion, setNotificacion] = useState({ visible: false, mensaje: '', tipo: 'success' });

  const mostrarNotificacion = (mensaje, tipo = 'success') => {
    setNotificacion({ visible: true, mensaje, tipo });
    setTimeout(() => {
      setNotificacion({ visible: false, mensaje: '', tipo: 'success' });
    }, 3000);
  };

  const normalizarTexto = (valor) => String(valor || '').trim().toUpperCase();

  // Cargar catálogo al inicio (caché)
  useEffect(() => {
    cargarCatalogo();
  }, []);

  // Cargar productos desde la API
  const cargarProductos = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Pasar filtro de sucursal al backend si está seleccionado
      const params = {};
      if (filtros.sucursal && filtros.sucursal !== '') {
        params.sucursal = filtros.sucursal;
      }
      
      const response = await productoService.getProductos(params);

      // Mostrar tambien productos con stock en 0 para poder reponer sin recrearlos.
      setProductos(response.data || []);
    } catch (error) {
      console.error('Error cargando productos:', error);
      setError('Error al cargar los productos');
    } finally {
      setLoading(false);
    }
  }, [filtros.sucursal]);

  useEffect(() => {
    cargarProductos();
  }, [cargarProductos]);

  // Limpiar filtros al cambiar de categoría
  useEffect(() => {
    setFiltros({
      tipo: '',
      modelo: '',
      medidas: '',
      madera: '',
      tapizado: '',
      lustre: '',
      cantidadTapizado: '',
      sucursal: user?.rol === 'admin' ? '' : (user?.sucursal?.toUpperCase() || ''),
      sku: '',
      proveedor: ''
    });
    setPaginaActual(1); // Resetear a página 1 al cambiar de categoría
  }, [categoriaSeleccionada, user]);

  // Cargar catálogo (solo una vez al inicio)
  const cargarCatalogo = async () => {
    try {
      const data = await api.get('/catalogo');
      // Filtrar ADICIONALES - no se deben mostrar en la vista de productos
      const catalogoFiltrado = (data || []).filter(item => item.categoria !== 'ADICIONALES');
      setCatalogo(catalogoFiltrado);
    } catch (error) {
      console.error('Error cargando catálogo:', error);
    }
  };

  // Abrir modal (ya no carga el catálogo, usa el caché)
  const abrirModal = () => {
    // Pre-seleccionar la categoría actual
    setNuevoProducto(prev => ({
      ...prev,
      categoria: categoriaSeleccionada
    }));
    setModalAbierto(true);
  };

  const cerrarModal = () => {
    setModalAbierto(false);
    setNuevoProducto({
      categoria: categoriaSeleccionada, // Pre-seleccionar la categoría actual
      tipo: '',
      modelo: '',
      medidas: '',
      madera: '',
      tapizado: '',
      cantidadTapizado: '',
      lustre: '',
      stock: 0,
      sucursal: ''
    });
  };

  // Ya no necesitamos mapear lustre, usamos directamente SIN LUSTRE, NATURAL, COMUN

  // Obtener opciones disponibles según las selecciones previas
  const obtenerOpcionesCascada = () => {
    // 1. CATEGORIAS: siempre basado en categoriaSeleccionada (no nuevoProducto.categoria)
    const categorias = [categoriaSeleccionada];

    // 2. TIPOS: filtrados por categoría seleccionada
    let catalogoFiltrado = catalogo.filter(c => c.categoria === categoriaSeleccionada);
    const tipos = [...new Set(catalogoFiltrado.map(c => c.tipo))];

    // 3. MODELOS: filtrados por tipo seleccionado
    if (nuevoProducto.tipo) {
      catalogoFiltrado = catalogoFiltrado.filter(c => c.tipo === nuevoProducto.tipo);
    }
    const modelos = [...new Set(catalogoFiltrado.map(c => c.modelo))];

    // 4. MEDIDAS: extraídas de variantes de TODOS los items con el modelo seleccionado
    let medidas = [];
    if (nuevoProducto.modelo) {
      const itemsCatalogo = catalogoFiltrado.filter(c => c.modelo === nuevoProducto.modelo);
      if (itemsCatalogo.length > 0) {
        // Recopilar variantes de TODOS los items con este modelo
        const todasLasVariantes = itemsCatalogo.flatMap(item => item.variantes || []);
        const todasLasMedidas = todasLasVariantes.map(v => v.medidas);
        medidas = [...new Set(todasLasMedidas.filter(Boolean))];
      }
    }

    // 5A. MADERAS (para no-SOFA): extraídas de variantes con la medida seleccionada
    let maderas = [];
    if (categoriaSeleccionada !== 'SOFA') {
      if (nuevoProducto.modelo) {
        const itemsCatalogo = catalogoFiltrado.filter(c => c.modelo === nuevoProducto.modelo);
        if (itemsCatalogo.length > 0) {
          const todasLasVariantes = itemsCatalogo.flatMap(item => item.variantes || []);
          // Para SILLA (sin medidas): filtrar solo por modelo
          if (categoriaSeleccionada === 'SILLA') {
            maderas = [...new Set(todasLasVariantes.map(v => v.madera).filter(Boolean))];
          } 
          // Para MUEBLES y RESPALDO (con medidas): filtrar por medidas también
          else if (nuevoProducto.medidas) {
            maderas = [...new Set(
              todasLasVariantes
                .filter(v => v.medidas === nuevoProducto.medidas)
                .map(v => v.madera)
                .filter(Boolean)
            )];
          }
        }
      }
    }

    // 5B. TAPIZADOS (para RESPALDO y SILLA): extraídos de variantes con madera seleccionada
    let tapizados = [];
    if (categoriaSeleccionada === 'RESPALDO') {
      if (nuevoProducto.modelo && nuevoProducto.medidas && nuevoProducto.madera) {
        const itemsCatalogo = catalogoFiltrado.filter(c => c.modelo === nuevoProducto.modelo);
        if (itemsCatalogo.length > 0) {
          const todasLasVariantes = itemsCatalogo.flatMap(item => item.variantes || []);
          tapizados = [...new Set(
            todasLasVariantes
              .filter(v => v.medidas === nuevoProducto.medidas && v.madera === nuevoProducto.madera)
              .map(v => v.tapizado)
              .filter(Boolean)
          )];
        }
      }
    } else if (categoriaSeleccionada === 'SILLA') {
      // Para SILLA: extraer de variantes con madera seleccionada (sin medidas)
      if (nuevoProducto.modelo && nuevoProducto.madera) {
        const itemsCatalogo = catalogoFiltrado.filter(c => c.modelo === nuevoProducto.modelo);
        if (itemsCatalogo.length > 0) {
          const todasLasVariantes = itemsCatalogo.flatMap(item => item.variantes || []);
          tapizados = [...new Set(
            todasLasVariantes
              .filter(v => v.madera === nuevoProducto.madera)
              .map(v => v.tapizado)
              .filter(Boolean)
          )];
        }
      }
    }

    // 5C. CANTIDADES TAPIZADO (para RESPALDO, SILLA y SOFA)
    let cantidadesTapizado = [];
    if (categoriaSeleccionada === 'RESPALDO') {
      // Para RESPALDO: extraer de variantes con madera y tapizado seleccionados
      if (nuevoProducto.modelo && nuevoProducto.medidas && nuevoProducto.madera && nuevoProducto.tapizado) {
        const itemsCatalogo = catalogoFiltrado.filter(c => c.modelo === nuevoProducto.modelo);
        if (itemsCatalogo.length > 0) {
          const todasLasVariantes = itemsCatalogo.flatMap(item => item.variantes || []);
          cantidadesTapizado = [...new Set(
            todasLasVariantes
              .filter(v => 
                v.medidas === nuevoProducto.medidas && 
                v.madera === nuevoProducto.madera &&
                v.tapizado === nuevoProducto.tapizado
              )
              .map(v => v.cantidadTapizado)
              .filter(Boolean)
          )];
        }
      }
    } else if (categoriaSeleccionada === 'SILLA') {
      // Para SILLA: extraer de variantes con madera y tapizado seleccionados
      if (nuevoProducto.modelo && nuevoProducto.madera && nuevoProducto.tapizado) {
        const itemsCatalogo = catalogoFiltrado.filter(c => c.modelo === nuevoProducto.modelo);
        if (itemsCatalogo.length > 0) {
          const todasLasVariantes = itemsCatalogo.flatMap(item => item.variantes || []);
          cantidadesTapizado = [...new Set(
            todasLasVariantes
              .filter(v => v.madera === nuevoProducto.madera && v.tapizado === nuevoProducto.tapizado)
              .map(v => v.cantidadTapizado)
              .filter(Boolean)
          )];
        }
      }
    } else if (categoriaSeleccionada === 'SOFA') {
      // Para SOFA: extraer de variantes con medidas seleccionadas
      if (nuevoProducto.modelo && nuevoProducto.medidas) {
        const itemsCatalogo = catalogoFiltrado.filter(c => c.modelo === nuevoProducto.modelo);
        if (itemsCatalogo.length > 0) {
          const todasLasVariantes = itemsCatalogo.flatMap(item => item.variantes || []);
          cantidadesTapizado = [...new Set(
            todasLasVariantes
              .filter(v => v.medidas === nuevoProducto.medidas)
              .map(v => v.cantidadTapizado)
              .filter(Boolean)
          )];
        }
      }
    }

    // 6. LUSTRES: extraídos según la categoría
    let lustres = [];
    if (nuevoProducto.modelo) {
      const itemsCatalogo = catalogoFiltrado.filter(c => c.modelo === nuevoProducto.modelo);
      if (itemsCatalogo.length > 0) {
        const todasLasVariantes = itemsCatalogo.flatMap(item => item.variantes || []);
        if (categoriaSeleccionada === 'SOFA') {
          // Para SOFA: filtrar por medidas y cantidadTapizado
          if (nuevoProducto.medidas && nuevoProducto.cantidadTapizado) {
            lustres = [...new Set(
              todasLasVariantes
                .filter(v => 
                  v.medidas === nuevoProducto.medidas && 
                  v.cantidadTapizado === nuevoProducto.cantidadTapizado
                )
                .map(v => v.lustre)
                .filter(Boolean)
            )];
          }
        } else if (categoriaSeleccionada === 'SILLA') {
          // Para SILLA: filtrar por madera, tapizado y cantidadTapizado
          if (nuevoProducto.madera && nuevoProducto.tapizado && nuevoProducto.cantidadTapizado) {
            lustres = [...new Set(
              todasLasVariantes
                .filter(v => 
                  v.madera === nuevoProducto.madera && 
                  v.tapizado === nuevoProducto.tapizado &&
                  v.cantidadTapizado === nuevoProducto.cantidadTapizado
                )
                .map(v => v.lustre)
                .filter(Boolean)
            )];
          }
        } else if (categoriaSeleccionada === 'RESPALDO') {
          // Para RESPALDO: filtrar por medidas, madera, tapizado y cantidadTapizado
          if (nuevoProducto.medidas && nuevoProducto.madera && nuevoProducto.tapizado && nuevoProducto.cantidadTapizado) {
            lustres = [...new Set(
              todasLasVariantes
                .filter(v => 
                  v.medidas === nuevoProducto.medidas && 
                  v.madera === nuevoProducto.madera && 
                  v.tapizado === nuevoProducto.tapizado &&
                  v.cantidadTapizado === nuevoProducto.cantidadTapizado
                )
                .map(v => v.lustre)
                .filter(Boolean)
            )];
          }
        } else { // MUEBLES
          // Para MUEBLES: filtrar por medidas y madera
          if (nuevoProducto.medidas && nuevoProducto.madera) {
            lustres = [...new Set(
              todasLasVariantes
                .filter(v => v.medidas === nuevoProducto.medidas && v.madera === nuevoProducto.madera)
                .map(v => v.lustre)
                .filter(Boolean)
            )];
          }
        }
      }
    }

    // TAPIZADO: del item de catálogo seleccionado
    let requireTapizado = false;
    if (nuevoProducto.modelo) {
      const itemsCatalogo = catalogoFiltrado.filter(c => c.modelo === nuevoProducto.modelo);
      if (itemsCatalogo.length > 0) {
        // Si alguno de los items requiere tapizado, marcarlo como true
        requireTapizado = itemsCatalogo.some(item => item.requireTapizado);
      }
    }

    // Para DECORACION: extraer modelos, SKUs y proveedores
    let skus = [];
    let proveedores = [];
    if (categoriaSeleccionada === 'DECORACION') {
      // SKUs: si hay modelo seleccionado, filtrar por modelo; si no, mostrar todos los SKUs
      if (nuevoProducto.modelo) {
        const itemsCatalogo = catalogoFiltrado.filter(c => c.modelo === nuevoProducto.modelo);
        if (itemsCatalogo.length > 0) {
          const todasLasVariantes = itemsCatalogo.flatMap(item => item.variantes || []);
          skus = [...new Set(todasLasVariantes.map(v => v.sku).filter(Boolean))];
        }
      } else if (catalogoFiltrado.length > 0) {
        const todasLasVariantes = catalogoFiltrado.flatMap(item => item.variantes || []);
        skus = [...new Set(todasLasVariantes.map(v => v.sku).filter(Boolean))];
      }

      // Proveedores: lista general de todos los proveedores en DECORACION
      if (catalogoFiltrado.length > 0) {
        const todasLasVariantes = catalogoFiltrado.flatMap(item => item.variantes || []);
        proveedores = [...new Set(todasLasVariantes.map(v => v.proveedor).filter(Boolean))];
      }
    }

    return { categorias, tipos, modelos, medidas, maderas, tapizados, cantidadesTapizado, lustres, requireTapizado, skus, proveedores };
  };

  const handleCrearProducto = async () => {
    // Flujo especial para DECORACION
    if (categoriaSeleccionada === 'DECORACION') {
      // Validar campos requeridos para DECORACION
      if (!nuevoProducto.modelo || !nuevoProducto.sku || !nuevoProducto.proveedor) {
        mostrarNotificacion('⚠️ Por favor complete todos los campos requeridos (MODELO, SKU, PROVEEDOR)', 'error');
        return;
      }

      if (nuevoProducto.stock <= 0) {
        mostrarNotificacion('⚠️ El stock debe ser mayor a 0', 'error');
        return;
      }

      // Determinar la sucursal
      const sucursalFinal = user?.rol === 'admin' ? nuevoProducto.sucursal : user?.sucursal;
      
      if (!sucursalFinal) {
        mostrarNotificacion('⚠️ Debe seleccionar una sucursal', 'error');
        return;
      }

      try {
        setCreando(true);

        const modeloNorm = normalizarTexto(nuevoProducto.modelo);
        const skuNorm = normalizarTexto(nuevoProducto.sku);
        const proveedorNorm = normalizarTexto(nuevoProducto.proveedor);

        // Buscar en TODOS los items de catálogo que tengan ese modelo DECORACION
        const itemsCatalogoModelo = catalogo.filter(c =>
          c.categoria === 'DECORACION' &&
          normalizarTexto(c.modelo) === modeloNorm
        );

        if (itemsCatalogoModelo.length === 0) {
          mostrarNotificacion(`⚠️ No se encontró "${nuevoProducto.modelo}" en el catálogo de DECORACION. Debe importarlo primero.`, 'error');
          return;
        }

        // Buscar variante por SKU + proveedor entre todos los items del modelo
        let itemCatalogo = null;
        let variante = null;

        for (const item of itemsCatalogoModelo) {
          const encontrada = (item.variantes || []).find((v) => {
            const skuVar = normalizarTexto(v?.sku);
            const proveedorVar = normalizarTexto(v?.proveedor);
            return skuVar === skuNorm && proveedorVar === proveedorNorm;
          });

          if (encontrada) {
            itemCatalogo = item;
            variante = encontrada;
            break;
          }
        }

        // Fallback: si no matcheó por proveedor, probar sólo por SKU (mismo modelo)
        if (!variante) {
          for (const item of itemsCatalogoModelo) {
            const encontrada = (item.variantes || []).find(
              (v) => normalizarTexto(v?.sku) === skuNorm
            );
            if (encontrada) {
              itemCatalogo = item;
              variante = encontrada;
              break;
            }
          }
        }

        if (!variante) {
          mostrarNotificacion(`⚠️ No se encontró el SKU "${nuevoProducto.sku}" en el catálogo para este modelo.`, 'error');
          return;
        }

        // Verificar si ya existe este producto en el sistema
        const productoExistente = productos.find(p =>
          String(p.catalogoRef?._id || p.catalogoRef || '') === String(itemCatalogo._id) &&
          normalizarTexto(p.varianteSeleccionada?.sku) === skuNorm
        );

        if (productoExistente) {
          // Actualizar stock del producto existente
          const stockSucursal = productoExistente.stockPorSucursal?.find(
            s => s.sucursal === sucursalFinal
          );
          const stockActual = stockSucursal ? stockSucursal.cantidad : 0;

          const stockPorSucursalActualizado = productoExistente.stockPorSucursal?.filter(
            s => s.sucursal !== sucursalFinal
          ) || [];
          stockPorSucursalActualizado.push({
            sucursal: sucursalFinal,
            cantidad: stockActual + nuevoProducto.stock
          });

          const productoData = {
            ...productoExistente,
            stockPorSucursal: stockPorSucursalActualizado
          };

          await productoService.updateProducto(productoExistente._id, productoData);
        } else {
          // Crear producto nuevo
          const productoData = {
            catalogoRef: itemCatalogo._id,
            categoria: 'DECORACION',
            tipo: 'DECORACION',
            modelo: nuevoProducto.modelo,
            varianteSeleccionada: {
              sku: variante.sku,
              proveedor: variante.proveedor
            },
            sucursalPropietaria: sucursalFinal,
            stockPorSucursal: [
              {
                sucursal: sucursalFinal,
                cantidad: nuevoProducto.stock
              }
            ]
          };

          const nuevoProductoCreado = await productoService.createProducto(productoData);
          // Agregar al estado local sin recargar
          setProductos(prevProductos => [...prevProductos, nuevoProductoCreado.data]);
        }

        cerrarModal();
        mostrarNotificacion('✅ Decoración creada exitosamente', 'success');
      } catch (error) {
        console.error('Error creando decoración:', error);
        mostrarNotificacion('❌ ' + (error.response?.data?.message || error.message), 'error');
      } finally {
        setCreando(false);
      }
      return;
    }

    // Flujo para MUEBLES, SILLAS, SOFAS, etc. (original)
    // Validar campos según categoría
    const camposRequeridos = [categoriaSeleccionada, nuevoProducto.tipo, nuevoProducto.modelo, nuevoProducto.lustre];
    
    // Agregar validaciones específicas según categoría
    if (categoriaSeleccionada === 'SOFA') {
      camposRequeridos.push(nuevoProducto.medidas, nuevoProducto.cantidadTapizado);
    } else if (categoriaSeleccionada === 'RESPALDO') {
      camposRequeridos.push(nuevoProducto.medidas, nuevoProducto.madera, nuevoProducto.tapizado, nuevoProducto.cantidadTapizado);
    } else if (categoriaSeleccionada === 'SILLA') {
      camposRequeridos.push(nuevoProducto.madera, nuevoProducto.tapizado, nuevoProducto.cantidadTapizado);
    } else { // MUEBLES
      camposRequeridos.push(nuevoProducto.medidas, nuevoProducto.madera);
    }

    if (camposRequeridos.some(campo => !campo)) {
      mostrarNotificacion('⚠️ Por favor complete todos los campos requeridos', 'error');
      return;
    }

    if (nuevoProducto.stock <= 0) {
      mostrarNotificacion('⚠️ El stock debe ser mayor a 0', 'error');
      return;
    }

    // Determinar la sucursal: si es admin usa la seleccionada, sino usa la del usuario
    const sucursalFinal = user?.rol === 'admin' ? nuevoProducto.sucursal : user?.sucursal;
    
    if (!sucursalFinal) {
      mostrarNotificacion('⚠️ Debe seleccionar una sucursal', 'error');
      return;
    }

    try {
      setCreando(true);

      // Buscar el catalogoId y la variante correspondiente
      const itemsCatalogo = catalogo.filter(c => 
        c.categoria === categoriaSeleccionada &&
        c.tipo === nuevoProducto.tipo &&
        c.modelo === nuevoProducto.modelo
      );

      if (itemsCatalogo.length === 0) {
        mostrarNotificacion('⚠️ No se encontró el producto en el catálogo', 'error');
        return;
      }

      // Buscar variante según categoría en TODOS los items con este modelo
      let variante = null;
      let itemCatalogo = null;
      
      for (const item of itemsCatalogo) {
        let varianteEncontrada;
        
        if (categoriaSeleccionada === 'SOFA') {
          varianteEncontrada = item.variantes.find(v =>
            v.medidas === nuevoProducto.medidas &&
            v.cantidadTapizado === nuevoProducto.cantidadTapizado &&
            v.lustre === nuevoProducto.lustre
          );
        } else if (categoriaSeleccionada === 'SILLA') {
          varianteEncontrada = item.variantes.find(v =>
            v.madera === nuevoProducto.madera &&
            v.tapizado === nuevoProducto.tapizado &&
            v.cantidadTapizado === nuevoProducto.cantidadTapizado &&
            v.lustre === nuevoProducto.lustre
          );
        } else if (categoriaSeleccionada === 'RESPALDO') {
          varianteEncontrada = item.variantes.find(v =>
            v.medidas === nuevoProducto.medidas &&
            v.madera === nuevoProducto.madera &&
            v.tapizado === nuevoProducto.tapizado &&
            v.cantidadTapizado === nuevoProducto.cantidadTapizado &&
            v.lustre === nuevoProducto.lustre
          );
        } else { // MUEBLES
          varianteEncontrada = item.variantes.find(v =>
            v.medidas === nuevoProducto.medidas &&
            v.madera === nuevoProducto.madera &&
            v.lustre === nuevoProducto.lustre
          );
        }
        
        if (varianteEncontrada) {
          variante = varianteEncontrada;
          itemCatalogo = item;
          break;
        }
      }
      if (!variante) {
        mostrarNotificacion('⚠️ No se encontró la variante seleccionada en el catálogo', 'error');
        return;
      }

      // Verificar si ya existe un producto con estas características
      let productoExistente;
      if (categoriaSeleccionada === 'SOFA') {
        productoExistente = productos.find(p =>
          p.catalogoRef === itemCatalogo._id &&
          p.varianteSeleccionada?.medidas === nuevoProducto.medidas &&
          p.varianteSeleccionada?.cantidadTapizado === nuevoProducto.cantidadTapizado &&
          p.varianteSeleccionada?.lustre === nuevoProducto.lustre
        );
      } else if (categoriaSeleccionada === 'SILLA') {
        productoExistente = productos.find(p =>
          p.catalogoRef === itemCatalogo._id &&
          p.varianteSeleccionada?.madera === nuevoProducto.madera &&
          p.varianteSeleccionada?.tapizado === nuevoProducto.tapizado &&
          p.varianteSeleccionada?.cantidadTapizado === nuevoProducto.cantidadTapizado &&
          p.varianteSeleccionada?.lustre === nuevoProducto.lustre
        );
      } else if (categoriaSeleccionada === 'RESPALDO') {
        productoExistente = productos.find(p =>
          p.catalogoRef === itemCatalogo._id &&
          p.varianteSeleccionada?.medidas === nuevoProducto.medidas &&
          p.varianteSeleccionada?.madera === nuevoProducto.madera &&
          p.varianteSeleccionada?.tapizado === nuevoProducto.tapizado &&
          p.varianteSeleccionada?.cantidadTapizado === nuevoProducto.cantidadTapizado &&
          p.varianteSeleccionada?.lustre === nuevoProducto.lustre
        );
      } else { // MUEBLES
        productoExistente = productos.find(p =>
          p.catalogoRef === itemCatalogo._id &&
          p.varianteSeleccionada?.medidas === nuevoProducto.medidas &&
          p.varianteSeleccionada?.madera === nuevoProducto.madera &&
          p.varianteSeleccionada?.lustre === nuevoProducto.lustre
        );
      }

      if (productoExistente) {
        // Actualizar stock del producto existente en la sucursal seleccionada
        const stockSucursal = productoExistente.stockPorSucursal?.find(
          s => s.sucursal === sucursalFinal
        );
        const stockActual = stockSucursal ? stockSucursal.cantidad : 0;

        // Actualizar o agregar stock en la sucursal seleccionada
        const stockPorSucursalActualizado = productoExistente.stockPorSucursal?.filter(
          s => s.sucursal !== sucursalFinal
        ) || [];
        stockPorSucursalActualizado.push({
          sucursal: sucursalFinal,
          cantidad: stockActual + nuevoProducto.stock
        });

        const productoData = {
          ...productoExistente,
          stockPorSucursal: stockPorSucursalActualizado
        };

        await productoService.updateProducto(productoExistente._id, productoData);
      } else {
        // Crear variante seleccionada según categoría
        const varianteSeleccionada = {
          lustre: nuevoProducto.lustre
        };

        // Agregar campos según categoría
        if (categoriaSeleccionada === 'SOFA') {
          varianteSeleccionada.medidas = nuevoProducto.medidas;
          varianteSeleccionada.cantidadTapizado = nuevoProducto.cantidadTapizado;
        } else if (categoriaSeleccionada === 'SILLA') {
          varianteSeleccionada.madera = nuevoProducto.madera;
          varianteSeleccionada.tapizado = nuevoProducto.tapizado;
          varianteSeleccionada.cantidadTapizado = nuevoProducto.cantidadTapizado;
        } else if (categoriaSeleccionada === 'RESPALDO') {
          varianteSeleccionada.medidas = nuevoProducto.medidas;
          varianteSeleccionada.madera = nuevoProducto.madera;
          varianteSeleccionada.tapizado = nuevoProducto.tapizado;
          varianteSeleccionada.cantidadTapizado = nuevoProducto.cantidadTapizado;
        } else { // MUEBLES
          varianteSeleccionada.medidas = nuevoProducto.medidas;
          varianteSeleccionada.madera = nuevoProducto.madera;
        }

        // Crear el producto nuevo
        const productoData = {
          catalogoRef: itemCatalogo._id,
          categoria: categoriaSeleccionada,
          tipo: nuevoProducto.tipo,
          modelo: nuevoProducto.modelo,
          varianteSeleccionada: varianteSeleccionada,
          sucursalPropietaria: sucursalFinal,
          stockPorSucursal: [
            {
              sucursal: sucursalFinal,
              cantidad: nuevoProducto.stock
            }
          ]
        };

        const nuevoProductoCreado = await productoService.createProducto(productoData);
        // Agregar al estado local sin recargar
        setProductos(prevProductos => [...prevProductos, nuevoProductoCreado.data]);
      }
      
      cerrarModal();
      mostrarNotificacion('✅ Producto creado exitosamente', 'success');
    } catch (error) {
      console.error('Error creando producto:', error);
      mostrarNotificacion('❌ ' + (error.response?.data?.message || error.message), 'error');
    } finally {
      setCreando(false);
    }
  };

  const handleEditarProducto = (producto, sucursalFila) => {
    // Verificar permisos
    if (!puedeModificarProducto(producto, sucursalFila)) {
      mostrarNotificacion('⚠️ No tiene permisos para editar este producto. Solo puede editar productos de su sucursal.', 'error');
      return;
    }
    
    setProductoEditando(producto);
    
    // Usar la sucursal de la fila seleccionada
    setSucursalEditar(sucursalFila);
    const stockEnSucursal = producto.stockPorSucursal?.find(
      s => s.sucursal.toLowerCase() === sucursalFila.toLowerCase()
    );
    setStockEditar(stockEnSucursal ? stockEnSucursal.cantidad : 0);
    
    setModalEditar(true);
  };

  const handleGuardarEdicion = async () => {
    if (stockEditar < 0) {
      return;
    }

    if (!sucursalEditar) {
      mostrarNotificacion('⚠️ Debe seleccionar una sucursal', 'error');
      return;
    }

    try {
      setCreando(true);
      
      // Actualizar solo el stock de la sucursal seleccionada
      const stockPorSucursalActualizado = productoEditando.stockPorSucursal?.filter(
        s => s.sucursal.toLowerCase() !== sucursalEditar.toLowerCase()
      ) || [];
      
      // Agregar el stock actualizado de esta sucursal
      stockPorSucursalActualizado.push({
        sucursal: sucursalEditar,
        cantidad: stockEditar
      });

      const productoData = {
        ...productoEditando,
        stockPorSucursal: stockPorSucursalActualizado
      };

      const productoActualizado = await productoService.updateProducto(productoEditando._id, productoData);
      
      // Actualizar el producto en el estado local
      setProductos(prevProductos => 
        prevProductos.map(p => p._id === productoEditando._id ? productoActualizado.data : p)
      );
      
      setModalEditar(false);
      setProductoEditando(null);
      mostrarNotificacion('✅ Stock actualizado exitosamente', 'success');
    } catch (error) {
      console.error('Error actualizando producto:', error);
      mostrarNotificacion('❌ Error al actualizar el producto', 'error');
    } finally {
      setCreando(false);
    }
  };

  const handleEliminarProducto = async (producto, sucursalFila) => {
    // Verificar permisos
    if (!puedeModificarProducto(producto, sucursalFila)) {
      mostrarNotificacion('⚠️ No tiene permisos para eliminar este producto. Solo puede eliminar productos de su sucursal.', 'error');
      return;
    }
    
    // Verificar si el producto tiene stock en otras sucursales
    const otrosSucursales = producto.stockPorSucursal?.filter(
      stock => stock.sucursal !== sucursalFila
    ) || [];

    let mensaje = `¿Está seguro que desea eliminar el producto ${producto.tipo} ${producto.modelo} de la sucursal ${sucursalFila.toUpperCase()}?`;
    if (otrosSucursales.length > 0) {
      mensaje = `Este producto también tiene stock en: ${otrosSucursales.map(s => s.sucursal).join(', ')}. \n\nSolo se eliminará el stock de ${sucursalFila.toUpperCase()}. ¿Desea continuar?`;
    }

    const confirmado = await showConfirm(mensaje, {
      title: 'Eliminar producto',
      confirmLabel: 'Eliminar',
    });
    if (!confirmado) return;

    try {
      await productoService.deleteProducto(producto._id, sucursalFila);
      
      // Actualizar el estado local sin recargar
      setProductos(prevProductos => {
        return prevProductos.map(p => {
          if (p._id === producto._id) {
            // Filtrar la sucursal eliminada
            const nuevoStock = p.stockPorSucursal.filter(s => s.sucursal !== sucursalFila);
            if (nuevoStock.length === 0) {
              // Si no quedan sucursales, eliminar el producto
              return null;
            }
            return { ...p, stockPorSucursal: nuevoStock };
          }
          return p;
        }).filter(Boolean); // Eliminar nulls
      });
      
      mostrarNotificacion('✅ Producto eliminado exitosamente', 'success');
    } catch (error) {
      console.error('Error eliminando producto:', error);
      mostrarNotificacion('❌ Error al eliminar el producto', 'error');
    }
  };

  const handleFiltroChange = (campo, valor) => {
    setFiltros(prev => ({ ...prev, [campo]: valor }));
    
    // Si cambia el filtro de sucursal, recargar productos del backend
    // (el useEffect se encargará de recargar cuando filtros.sucursal cambie)
  };

  // Verificar si el usuario puede editar/eliminar un producto
  const puedeModificarProducto = (producto, sucursalFila) => {
    // Admin puede modificar cualquier producto
    if (user?.rol === 'admin') return true;
    
    // Usuario de sucursal solo puede modificar si la fila es de SU sucursal
    return sucursalFila?.toLowerCase() === user?.sucursal?.toLowerCase();
  };

  const aplicarFiltros = () => {
    return productos.filter(producto => {
      // Filtrar por categoría seleccionada
      if (producto.categoria !== categoriaSeleccionada) return false;
      
      if (filtros.tipo && filtros.tipo !== producto.tipo) return false;
      if (filtros.modelo && filtros.modelo !== producto.modelo) return false;
      if (filtros.tapizado) {
        const tapizadoProducto = producto.requireTapizado ? 'SÍ' : 'NO';
        if (filtros.tapizado !== tapizadoProducto) return false;
      }
      
      // Filtrar por madera en varianteSeleccionada
      if (filtros.madera && producto.varianteSeleccionada?.madera !== filtros.madera) {
        return false;
      }
      
      // Filtrar por tapizado
      if (filtros.tapizado) {
        if (categoriaSeleccionada === 'RESPALDO') {
          // Para RESPALDO, comparar con el valor de tapizado en varianteSeleccionada
          if (producto.varianteSeleccionada?.tapizado !== filtros.tapizado) {
            return false;
          }
        } else {
          // Para MUEBLES y SILLA, comparar con requireTapizado
          const tapizadoProducto = producto.requireTapizado ? 'SÍ' : 'NO';
          if (tapizadoProducto !== filtros.tapizado) {
            return false;
          }
        }
      }
      
      // Filtrar por medidas en varianteSeleccionada
      if (filtros.medidas && producto.varianteSeleccionada?.medidas !== filtros.medidas) {
        return false;
      }
      
      // Filtrar por lustre en varianteSeleccionada
      if (filtros.lustre && producto.varianteSeleccionada?.lustre !== filtros.lustre) {
        return false;
      }
      
      // Filtrar por cantidad de tapizado (para RESPALDO, SILLA, SOFA)
      if (filtros.cantidadTapizado && producto.varianteSeleccionada?.cantidadTapizado !== filtros.cantidadTapizado) {
        return false;
      }
      
      // Filtrar por SKU (para DECORACION)
      if (filtros.sku && producto.varianteSeleccionada?.sku !== filtros.sku) {
        return false;
      }
      
      // Filtrar por PROVEEDOR (para DECORACION)
      if (filtros.proveedor && producto.varianteSeleccionada?.proveedor !== filtros.proveedor) {
        return false;
      }
      
      // NO filtrar por sucursal aquí - el backend ya lo hace
      
      return true;
    });
  };

  // Obtener opciones únicas para cada filtro basadas en productos filtrados
  const obtenerOpcionesUnicas = () => {
    // Primero aplicamos los filtros para obtener los productos visibles
    const productosVisibles = aplicarFiltros();
    
    const maderas = new Set();
    const medidas = new Set();
    const lustres = new Set();
    const cantidadesTapizado = new Set();
    const tapizados = new Set();
    const skus = new Set();
    const proveedores = new Set();
    
    // Para sucursales, mostrar siempre las 3 sucursales válidas
    // para que los usuarios puedan cambiar entre ellas
    const sucursales = ['PARANA', 'ROSARIO', 'ESPERANZA'];
    
    productosVisibles.forEach(p => {
      if (p.varianteSeleccionada?.madera) maderas.add(p.varianteSeleccionada.madera);
      if (p.varianteSeleccionada?.medidas) medidas.add(p.varianteSeleccionada.medidas);
      if (p.varianteSeleccionada?.lustre) lustres.add(p.varianteSeleccionada.lustre);
      if (p.varianteSeleccionada?.cantidadTapizado) cantidadesTapizado.add(p.varianteSeleccionada.cantidadTapizado);
      // Para DECORACION, agregar SKU y PROVEEDOR
      if (categoriaSeleccionada === 'DECORACION') {
        if (p.varianteSeleccionada?.sku) skus.add(p.varianteSeleccionada.sku);
        if (p.varianteSeleccionada?.proveedor) proveedores.add(p.varianteSeleccionada.proveedor);
      }
      // Para RESPALDO, extraer tapizados reales; para otros, usar requireTapizado
      if (categoriaSeleccionada === 'RESPALDO' && p.varianteSeleccionada?.tapizado) {
        tapizados.add(p.varianteSeleccionada.tapizado);
      } else if (categoriaSeleccionada !== 'RESPALDO' && categoriaSeleccionada !== 'DECORACION') {
        tapizados.add(p.requireTapizado ? 'SÍ' : 'NO');
      }
    });

    return {
      tipos: [...new Set(productosVisibles.map(p => p.tipo).filter(Boolean))],
      modelos: [...new Set(productosVisibles.map(p => p.modelo).filter(Boolean))],
      medidas: [...medidas],
      maderas: [...maderas],
      tapizados: categoriaSeleccionada === 'RESPALDO' ? [...tapizados].sort() : ['NO', 'SÍ'],
      lustres: [...lustres],
      cantidadesTapizado: [...cantidadesTapizado],
      sucursales: [...sucursales].sort(),
      skus: [...skus],
      proveedores: [...proveedores]
    };
  };

  if (loading) {
    return <LoadingSpinner label="Cargando productos..." />;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  const productosFiltrados = aplicarFiltros();
  const opciones = obtenerOpcionesUnicas();

  // Cálculos de paginación
  const totalItems = productosFiltrados.length;
  const totalPaginas = Math.ceil(totalItems / itemsPorPagina);
  const indiceInicio = (paginaActual - 1) * itemsPorPagina;
  const indiceFin = indiceInicio + itemsPorPagina;
  const productosPaginados = productosFiltrados.slice(indiceInicio, indiceFin);

  const irAPagina = (pagina) => {
    if (pagina >= 1 && pagina <= totalPaginas) {
      setPaginaActual(pagina);
    }
  };

  // Texto del botón según categoría
  const textoBtnNuevo = {
    'MUEBLES': '+ Nuevo Mueble',
    'RESPALDO': '+ Nuevo Respaldo',
    'SILLA': '+ Nueva Silla',
    'SOFA': '+ Nuevo Sofá',
    'DECORACION': '+ Nueva Decoración'
  };

  return (
    <div className="productos-container">
      {/* Notificación no invasiva */}
      {notificacion.visible && (
        <div className={`notificacion-toast ${notificacion.tipo}`}>
          {notificacion.mensaje}
        </div>
      )}

      <div className="productos-header">
        <h1>📦 Stock</h1>
        <button className="btn-primary" onClick={abrirModal}>
          {textoBtnNuevo[categoriaSeleccionada] || '+ Nuevo Producto'}
        </button>
      </div>

      {/* Tabs de categorías */}
      <div className="catalogo-tabs">
        <button 
          className={`tab-button ${categoriaSeleccionada === 'MUEBLES' ? 'active' : ''}`}
          onClick={() => setCategoriaSeleccionada('MUEBLES')}
        >
          MUEBLES
        </button>
        <button 
          className={`tab-button ${categoriaSeleccionada === 'RESPALDO' ? 'active' : ''}`}
          onClick={() => setCategoriaSeleccionada('RESPALDO')}
        >
          RESPALDOS
        </button>
        <button 
          className={`tab-button ${categoriaSeleccionada === 'SILLA' ? 'active' : ''}`}
          onClick={() => setCategoriaSeleccionada('SILLA')}
        >
          SILLAS
        </button>
        <button 
          className={`tab-button ${categoriaSeleccionada === 'SOFA' ? 'active' : ''}`}
          onClick={() => setCategoriaSeleccionada('SOFA')}
        >
          SOFÁS
        </button>
        <button 
          className={`tab-button ${categoriaSeleccionada === 'DECORACION' ? 'active' : ''}`}
          onClick={() => setCategoriaSeleccionada('DECORACION')}
        >
          DECORACIONES
        </button>
      </div>

      {/* Botón limpiar filtros */}
      {(filtros.tipo || filtros.modelo || filtros.medidas || filtros.madera || filtros.tapizado || filtros.lustre || filtros.cantidadTapizado || filtros.sku || filtros.proveedor || (user?.rol === 'admin' && filtros.sucursal)) && (
        <div style={{ marginBottom: '15px' }}>
          <button 
            className="btn-limpiar-filtros"
            onClick={() => setFiltros({ 
              tipo: '', 
              modelo: '', 
              medidas: '', 
              madera: '', 
              tapizado: '', 
              lustre: '', 
              cantidadTapizado: '',
              sucursal: user?.rol === 'admin' ? '' : (user?.sucursal?.toUpperCase() || ''), // Reset a sucursal del usuario si no es admin
              sku: '',
              proveedor: ''
            })}
          >
            ✕ Limpiar filtros
          </button>
        </div>
      )}

      <div className="productos-tabla">
        <table>
          <thead>
            <tr>
              {categoriaSeleccionada === 'DECORACION' ? (
                /* Columnas para DECORACION */
                <>
                  <th>
                    <div className="th-filtro">
                      MODELO
                      <SearchableSelect
                        value={filtros.modelo}
                        onChange={(value) => handleFiltroChange('modelo', value)}
                        options={['', ...opciones.modelos]}
                        placeholder="Todos"
                      />
                    </div>
                  </th>
                  <th>
                    <div className="th-filtro">
                      SKU
                      <SearchableSelect
                        value={filtros.sku}
                        onChange={(value) => handleFiltroChange('sku', value)}
                        options={['', ...opciones.skus]}
                        placeholder="Todos"
                      />
                    </div>
                  </th>
                  <th>
                    <div className="th-filtro">
                      PROVEEDOR
                      <SearchableSelect
                        value={filtros.proveedor}
                        onChange={(value) => handleFiltroChange('proveedor', value)}
                        options={['', ...opciones.proveedores]}
                        placeholder="Todos"
                      />
                    </div>
                  </th>
                  <th>
                    <div className="th-filtro">
                      SUCURSAL
                      <SearchableSelect
                        value={filtros.sucursal}
                        onChange={(value) => handleFiltroChange('sucursal', value)}
                        options={['', ...opciones.sucursales]}
                        placeholder="Todas"
                      />
                    </div>
                  </th>
                  <th>STOCK</th>
                  <th>ACCIONES</th>
                </>
              ) : (
                /* Columnas para MUEBLES, SILLAS, SOFAS, etc. */
                <>
              {/* TIPO - Común a todas las categorías */}
              <th>
                <div className="th-filtro">
                  TIPO
                  <SearchableSelect
                    value={filtros.tipo}
                    onChange={(value) => handleFiltroChange('tipo', value)}
                    options={['', ...opciones.tipos]}
                    placeholder="Todos"
                  />
                </div>
              </th>

              {/* MODELO - Común a todas las categorías */}
              <th>
                <div className="th-filtro">
                  MODELO
                  <SearchableSelect
                    value={filtros.modelo}
                    onChange={(value) => handleFiltroChange('modelo', value)}
                    options={['', ...opciones.modelos]}
                    placeholder="Todos"
                  />
                </div>
              </th>

              {/* MEDIDAS - Solo para MUEBLES, RESPALDO y SOFA */}
              {(categoriaSeleccionada === 'MUEBLES' || categoriaSeleccionada === 'RESPALDO' || categoriaSeleccionada === 'SOFA') && (
                <th>
                  <div className="th-filtro">
                    {categoriaSeleccionada === 'MUEBLES' ? 'MEDIDA' : 'MEDIDAS'}
                    <SearchableSelect
                      value={filtros.medidas}
                      onChange={(value) => handleFiltroChange('medidas', value)}
                      options={['', ...opciones.medidas]}
                      placeholder="Todas"
                    />
                  </div>
                </th>
              )}

              {/* MADERA - Para MUEBLES, RESPALDO y SILLA (no para SOFA) */}
              {categoriaSeleccionada !== 'SOFA' && (
                <th>
                  <div className="th-filtro">
                    MADERA
                    <SearchableSelect
                      value={filtros.madera}
                      onChange={(value) => handleFiltroChange('madera', value)}
                      options={['', ...opciones.maderas]}
                      placeholder="Todas"
                    />
                  </div>
                </th>
              )}

              {/* TAPIZADO - Para MUEBLES, RESPALDO y SILLA (no para SOFA) */}
              {categoriaSeleccionada !== 'SOFA' && (
                <th>
                  <div className="th-filtro">
                    TAPIZADO
                    <SearchableSelect
                      value={filtros.tapizado}
                      onChange={(value) => handleFiltroChange('tapizado', value)}
                      options={['', ...opciones.tapizados]}
                      placeholder="Todos"
                    />
                  </div>
                </th>
              )}

              {/* CANTIDAD - Solo para RESPALDO y SILLA */}
              {(categoriaSeleccionada === 'RESPALDO' || categoriaSeleccionada === 'SILLA') && (
                <th>
                  <div className="th-filtro">
                    CANTIDAD
                    <SearchableSelect
                      value={filtros.cantidadTapizado}
                      onChange={(value) => handleFiltroChange('cantidadTapizado', value)}
                      options={['', ...opciones.cantidadesTapizado]}
                      placeholder="Todas"
                    />
                  </div>
                </th>
              )}

              {/* CANT TELA/CUERO - Solo para SOFA */}
              {categoriaSeleccionada === 'SOFA' && (
                <th>
                  <div className="th-filtro">
                    CANT TELA/CUERO
                    <SearchableSelect
                      value={filtros.cantidadTapizado}
                      onChange={(value) => handleFiltroChange('cantidadTapizado', value)}
                      options={['', ...opciones.cantidadesTapizado]}
                      placeholder="Todas"
                    />
                  </div>
                </th>
              )}

              {/* LUSTRE para MUEBLES, RESPALDO, SILLA / MATERIAL para SOFA */}
              <th>
                <div className="th-filtro">
                  {categoriaSeleccionada === 'SOFA' ? 'MATERIAL' : 'LUSTRE'}
                  <SearchableSelect
                    value={filtros.lustre}
                    onChange={(value) => handleFiltroChange('lustre', value)}
                    options={['', ...opciones.lustres]}
                    placeholder="Todos"
                  />
                </div>
              </th>

              {/* SUCURSAL - Común a todas */}
              <th>
                <div className="th-filtro">
                  SUCURSAL
                  <SearchableSelect
                    value={filtros.sucursal}
                    onChange={(value) => handleFiltroChange('sucursal', value)}
                    options={['', ...opciones.sucursales]}
                    placeholder="Todas"
                  />
                </div>
              </th>

              {/* STOCK - Común a todas */}
              <th>STOCK</th>

              {/* ACCIONES - Común a todas */}
              <th>ACCIONES</th>
              </>
              )}
            </tr>
          </thead>
          <tbody>
            {productosFiltrados.length === 0 ? (
              <tr>
                <td colSpan="20" className="text-center">
                  {productos.length === 0 
                    ? 'No hay productos cargados'
                    : 'No se encontraron productos con los filtros aplicados'}
                </td>
              </tr>
            ) : (
              productosPaginados.flatMap((producto) => {
                // Mostrar todas las sucursales, incluyendo stock 0.
                const sucursalesAMostrar = producto.stockPorSucursal || [];

                if (!sucursalesAMostrar || sucursalesAMostrar.length === 0) {
                  return []; // No mostrar si no hay sucursales con stock
                }

                // Crear una fila por cada sucursal con stock
                return sucursalesAMostrar.map((stockSucursal) => (
                  <tr key={`${producto._id}-${stockSucursal.sucursal}`}>
                    {categoriaSeleccionada === 'DECORACION' ? (
                      /* Columnas para DECORACION */
                      <>
                        <td>{producto.modelo || '-'}</td>
                        <td>{producto.varianteSeleccionada?.sku || '-'}</td>
                        <td>{producto.varianteSeleccionada?.proveedor || '-'}</td>
                        <td>{stockSucursal.sucursal.toUpperCase()}</td>
                        <td>{stockSucursal.cantidad}</td>
                        <td>
                          <button 
                            className="btn-edit" 
                            title={puedeModificarProducto(producto, stockSucursal.sucursal) ? "Editar" : "No tiene permisos para editar"}
                            onClick={() => handleEditarProducto(producto, stockSucursal.sucursal)}
                            disabled={!puedeModificarProducto(producto, stockSucursal.sucursal)}
                            style={{ opacity: puedeModificarProducto(producto, stockSucursal.sucursal) ? 1 : 0.5, cursor: puedeModificarProducto(producto, stockSucursal.sucursal) ? 'pointer' : 'not-allowed' }}
                          >
                            ✏️
                          </button>
                          <button 
                            className="btn-delete" 
                            title={puedeModificarProducto(producto, stockSucursal.sucursal) ? "Eliminar" : "No tiene permisos para eliminar"}
                            onClick={() => handleEliminarProducto(producto, stockSucursal.sucursal)}
                            disabled={!puedeModificarProducto(producto, stockSucursal.sucursal)}
                            style={{ opacity: puedeModificarProducto(producto, stockSucursal.sucursal) ? 1 : 0.5, cursor: puedeModificarProducto(producto, stockSucursal.sucursal) ? 'pointer' : 'not-allowed' }}
                          >
                            🗑️
                          </button>
                        </td>
                      </>
                    ) : (
                      /* Columnas para MUEBLES, SILLAS, SOFAS, etc. */
                      <>
                    {/* TIPO - Común a todas */}
                    <td>{producto.tipo || '-'}</td>

                    {/* MODELO - Común a todas */}
                    <td>{producto.modelo || producto.nombre}</td>

                    {/* MEDIDAS - Para MUEBLES, RESPALDO y SOFA */}
                    {(categoriaSeleccionada === 'MUEBLES' || categoriaSeleccionada === 'RESPALDO' || categoriaSeleccionada === 'SOFA') && (
                      <td>{producto.varianteSeleccionada?.medidas || '-'}</td>
                    )}

                    {/* MADERA - Para MUEBLES, RESPALDO y SILLA */}
                    {categoriaSeleccionada !== 'SOFA' && (
                      <td>{producto.varianteSeleccionada?.madera || '-'}</td>
                    )}

                    {/* TAPIZADO - Para MUEBLES, RESPALDO y SILLA */}
                    {categoriaSeleccionada !== 'SOFA' && (
                      <td>
                        {(categoriaSeleccionada === 'RESPALDO' || categoriaSeleccionada === 'SILLA')
                          ? (producto.varianteSeleccionada?.tapizado || '-')
                          : (producto.requireTapizado ? 'SÍ' : 'NO')
                        }
                      </td>
                    )}

                    {/* CANTIDAD - Para RESPALDO y SILLA */}
                    {(categoriaSeleccionada === 'RESPALDO' || categoriaSeleccionada === 'SILLA') && (
                      <td>{producto.varianteSeleccionada?.cantidadTapizado || '-'}</td>
                    )}

                    {/* CANT TELA/CUERO - Para SOFA */}
                    {categoriaSeleccionada === 'SOFA' && (
                      <td>{producto.varianteSeleccionada?.cantidadTapizado || '-'}</td>
                    )}

                    {/* LUSTRE/MATERIAL */}
                    <td>{producto.varianteSeleccionada?.lustre || '-'}</td>

                    {/* SUCURSAL - Común a todas */}
                    <td>{stockSucursal.sucursal.toUpperCase()}</td>

                    {/* STOCK - Común a todas */}
                    <td>{stockSucursal.cantidad}</td>

                    {/* ACCIONES - Común a todas */}
                    <td>
                      <button 
                        className="btn-edit" 
                        title={puedeModificarProducto(producto, stockSucursal.sucursal) ? "Editar" : "No tiene permisos para editar"}
                        onClick={() => handleEditarProducto(producto, stockSucursal.sucursal)}
                        disabled={!puedeModificarProducto(producto, stockSucursal.sucursal)}
                        style={{ opacity: puedeModificarProducto(producto, stockSucursal.sucursal) ? 1 : 0.5, cursor: puedeModificarProducto(producto, stockSucursal.sucursal) ? 'pointer' : 'not-allowed' }}
                      >
                        ✏️
                      </button>
                      <button 
                        className="btn-delete" 
                        title={puedeModificarProducto(producto, stockSucursal.sucursal) ? "Eliminar" : "No tiene permisos para eliminar"}
                        onClick={() => handleEliminarProducto(producto, stockSucursal.sucursal)}
                        disabled={!puedeModificarProducto(producto, stockSucursal.sucursal)}
                        style={{ opacity: puedeModificarProducto(producto, stockSucursal.sucursal) ? 1 : 0.5, cursor: puedeModificarProducto(producto, stockSucursal.sucursal) ? 'pointer' : 'not-allowed' }}
                      >
                        🗑️
                      </button>
                    </td>
                    </>
                    )}
                  </tr>
                ));
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Controles de paginación */}
      {productosFiltrados.length > 0 && (
        <div className="paginacion-container">
          <div className="paginacion-info">
            Mostrando {indiceInicio + 1} - {Math.min(indiceFin, totalItems)} de {totalItems} productos
          </div>
          
          <div className="paginacion-controles">
            <button 
              onClick={() => irAPagina(1)}
              disabled={paginaActual === 1}
              className="btn-paginacion"
              title="Primera página"
            >
              ««
            </button>
            
            <button 
              onClick={() => irAPagina(paginaActual - 1)}
              disabled={paginaActual === 1}
              className="btn-paginacion"
              title="Página anterior"
            >
              ‹
            </button>
            
            <div className="paginacion-paginas">
              {Array.from({ length: totalPaginas }, (_, i) => i + 1)
                .filter(pagina => {
                  // Mostrar solo páginas cercanas a la actual
                  return pagina === 1 || 
                          pagina === totalPaginas || 
                          (pagina >= paginaActual - 2 && pagina <= paginaActual + 2);
                })
                .map((pagina, index, array) => (
                  <React.Fragment key={pagina}>
                    {index > 0 && array[index - 1] !== pagina - 1 && (
                      <span className="paginacion-ellipsis">...</span>
                    )}
                    <button
                      onClick={() => irAPagina(pagina)}
                      className={`btn-paginacion ${paginaActual === pagina ? 'active' : ''}`}
                    >
                      {pagina}
                    </button>
                  </React.Fragment>
                ))
              }
            </div>
            
            <button 
              onClick={() => irAPagina(paginaActual + 1)}
              disabled={paginaActual === totalPaginas}
              className="btn-paginacion"
              title="Página siguiente"
            >
              ›
            </button>
            
            <button 
              onClick={() => irAPagina(totalPaginas)}
              disabled={paginaActual === totalPaginas}
              className="btn-paginacion"
              title="Última página"
            >
              »»
            </button>
          </div>

          <div className="paginacion-selector">
            <label>Items por página:</label>
            <select 
              value={itemsPorPagina} 
              onChange={(e) => {
                setItemsPorPagina(Number(e.target.value));
                setPaginaActual(1);
              }}
              className="select-items-pagina"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>
      )}

      {/* Modal de crear producto */}
      {modalAbierto && (
        <div className="modal-overlay" onClick={cerrarModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Crear Nuevo Producto - {categoriaSeleccionada}</h2>
              <button className="modal-close" onClick={cerrarModal}>×</button>
            </div>
            
            <div className="modal-body">
              {categoriaSeleccionada === 'DECORACION' ? (
                /* Formulario específico para DECORACION */
                <>
                  <div className="form-group">
                    <label>MODELO *</label>
                    <SearchableSelect
                      value={nuevoProducto.modelo}
                      onChange={(value) => {
                        setNuevoProducto({
                          ...nuevoProducto,
                          modelo: value,
                          sku: '',
                          proveedor: '',
                          costo: 0
                        });
                        // Si existe el modelo en el catálogo, pre-cargar datos
                        const itemCatalogo = catalogo.find(
                          c => c.categoria === 'DECORACION' && c.modelo === value
                        );
                        if (itemCatalogo && itemCatalogo.variantes.length > 0) {
                          // Auto-seleccionar la primera variante si hay solo una
                          if (itemCatalogo.variantes.length === 1) {
                            const variante = itemCatalogo.variantes[0];
                            setNuevoProducto(prev => ({
                              ...prev,
                              modelo: value,
                              sku: variante.sku || '',
                              proveedor: variante.proveedor || '',
                              costo: variante.costo || 0
                            }));
                          }
                        }
                      }}
                      options={obtenerOpcionesCascada().modelos}
                      placeholder="Seleccione modelo"
                      disabled={creando}
                    />
                  </div>

                  <div className="form-group">
                    <label>SKU (Código) *</label>
                    <SearchableSelect
                      value={nuevoProducto.sku}
                      onChange={(value) => {
                        const itemsDecoracion = catalogo.filter(c => c.categoria === 'DECORACION');

                        // Si ya hay modelo seleccionado, priorizar ese modelo; si no, buscar en todo DECORACION.
                        let itemCatalogo = null;
                        let variante = null;

                        if (nuevoProducto.modelo) {
                          itemCatalogo = itemsDecoracion.find(c => c.modelo === nuevoProducto.modelo);
                          variante = itemCatalogo?.variantes?.find(v => v.sku === value);
                        }

                        if (!variante) {
                          for (const item of itemsDecoracion) {
                            const found = item.variantes?.find(v => v.sku === value);
                            if (found) {
                              itemCatalogo = item;
                              variante = found;
                              break;
                            }
                          }
                        }

                        setNuevoProducto(prev => ({
                          ...prev,
                          modelo: itemCatalogo?.modelo || prev.modelo,
                          sku: value,
                          proveedor: variante?.proveedor || '',
                          costo: variante?.costo || 0
                        }));
                      }}
                      options={obtenerOpcionesCascada().skus}
                      placeholder="Seleccione SKU"
                      disabled={creando}
                    />
                  </div>

                  <div className="form-group">
                    <label>PROVEEDOR *</label>
                    <SearchableSelect
                      value={nuevoProducto.proveedor}
                      onChange={(value) => setNuevoProducto({
                        ...nuevoProducto,
                        proveedor: value
                      })}
                      options={obtenerOpcionesCascada().proveedores}
                      placeholder="Seleccione proveedor"
                      disabled={creando}
                    />
                  </div>
                </>
              ) : (
                /* Formulario cascada para MUEBLES, SILLAS, SOFAS, etc. */
                <>
              {/* TIPO - Común a todas las categorías */}
              <div className="form-group">
                <label>TIPO *</label>
                <SearchableSelect
                  value={nuevoProducto.tipo}
                  onChange={(value) => setNuevoProducto({
                    ...nuevoProducto,
                    tipo: value,
                    modelo: '',
                    medidas: '',
                    madera: '',
                    tapizado: '',
                    cantidadTapizado: '',
                    lustre: ''
                  })}
                  options={obtenerOpcionesCascada().tipos}
                  placeholder="Seleccione tipo"
                  disabled={creando}
                />
              </div>

              {/* MODELO - Común a todas */}
              <div className="form-group">
                <label>MODELO *</label>
                <SearchableSelect
                  value={nuevoProducto.modelo}
                  onChange={(value) => setNuevoProducto({
                    ...nuevoProducto,
                    modelo: value,
                    medidas: '',
                    madera: '',
                    tapizado: '',
                    cantidadTapizado: '',
                    lustre: ''
                  })}
                  options={obtenerOpcionesCascada().modelos}
                  placeholder="Seleccione modelo"
                  disabled={!nuevoProducto.tipo || creando}
                />
              </div>

              {/* MEDIDAS - Para MUEBLES, RESPALDO y SOFA */}
              {(categoriaSeleccionada === 'MUEBLES' || categoriaSeleccionada === 'RESPALDO' || categoriaSeleccionada === 'SOFA') && (
                <div className="form-group">
                  <label>MEDIDAS *</label>
                  <SearchableSelect
                    value={nuevoProducto.medidas}
                    onChange={(value) => setNuevoProducto({
                      ...nuevoProducto,
                      medidas: value,
                      madera: '',
                      tapizado: '',
                      cantidadTapizado: '',
                      lustre: ''
                    })}
                    options={obtenerOpcionesCascada().medidas}
                    placeholder="Seleccione medidas"
                    disabled={!nuevoProducto.modelo || creando}
                  />
                </div>
              )}

              {/* MADERA - Para MUEBLES, RESPALDO y SILLA (no para SOFA) */}
              {categoriaSeleccionada !== 'SOFA' && (
                <div className="form-group">
                  <label>MADERA *</label>
                  <SearchableSelect
                    value={nuevoProducto.madera}
                    onChange={(value) => setNuevoProducto({
                      ...nuevoProducto,
                      madera: value,
                      tapizado: '',
                      cantidadTapizado: '',
                      lustre: ''
                    })}
                    options={obtenerOpcionesCascada().maderas}
                    placeholder="Seleccione madera"
                    disabled={
                      (categoriaSeleccionada === 'SILLA' ? !nuevoProducto.modelo : !nuevoProducto.medidas) || 
                      creando
                    }
                  />
                </div>
              )}

              {/* TAPIZADO - Para RESPALDO y SILLA */}
              {(categoriaSeleccionada === 'RESPALDO' || categoriaSeleccionada === 'SILLA') && (
                <div className="form-group">
                  <label>TAPIZADO *</label>
                  <SearchableSelect
                    value={nuevoProducto.tapizado}
                    onChange={(value) => setNuevoProducto({
                      ...nuevoProducto,
                      tapizado: value,
                      cantidadTapizado: '',
                      lustre: ''
                    })}
                    options={obtenerOpcionesCascada().tapizados}
                    placeholder="Seleccione tapizado"
                    disabled={!nuevoProducto.madera || creando}
                  />
                </div>
              )}

              {/* CANTIDAD TAPIZADO - Para RESPALDO y SILLA */}
              {(categoriaSeleccionada === 'RESPALDO' || categoriaSeleccionada === 'SILLA') && (
                <div className="form-group">
                  <label>CANTIDAD *</label>
                  <SearchableSelect
                    value={nuevoProducto.cantidadTapizado}
                    onChange={(value) => setNuevoProducto({
                      ...nuevoProducto,
                      cantidadTapizado: value,
                      lustre: ''
                    })}
                    options={obtenerOpcionesCascada().cantidadesTapizado}
                    placeholder="Seleccione cantidad"
                    disabled={!nuevoProducto.tapizado || creando}
                  />
                </div>
              )}

              {/* CANT TELA/CUERO - Para SOFA */}
              {categoriaSeleccionada === 'SOFA' && (
                <div className="form-group">
                  <label>CANT TELA/CUERO *</label>
                  <SearchableSelect
                    value={nuevoProducto.cantidadTapizado}
                    onChange={(value) => setNuevoProducto({
                      ...nuevoProducto,
                      cantidadTapizado: value,
                      lustre: ''
                    })}
                    options={obtenerOpcionesCascada().cantidadesTapizado}
                    placeholder="Seleccione cantidad"
                    disabled={!nuevoProducto.medidas || creando}
                  />
                </div>
              )}

              {/* LUSTRE para MUEBLES, RESPALDO, SILLA / MATERIAL para SOFA */}
              <div className="form-group">
                <label>{categoriaSeleccionada === 'SOFA' ? 'MATERIAL *' : 'LUSTRE *'}</label>
                <SearchableSelect
                  value={nuevoProducto.lustre}
                  onChange={(value) => setNuevoProducto({
                    ...nuevoProducto,
                    lustre: value
                  })}
                  options={obtenerOpcionesCascada().lustres}
                  placeholder={`Seleccione ${categoriaSeleccionada === 'SOFA' ? 'material' : 'lustre'}`}
                  disabled={
                    (categoriaSeleccionada === 'SOFA' ? !nuevoProducto.cantidadTapizado :
                     (categoriaSeleccionada === 'RESPALDO' || categoriaSeleccionada === 'SILLA') ? !nuevoProducto.cantidadTapizado :
                     !nuevoProducto.madera) || 
                    creando
                  }
                />
              </div>

              {/* Información sobre tapizado */}
              {obtenerOpcionesCascada().requireTapizado && categoriaSeleccionada !== 'SOFA' && (
                <div className="info-tapizado">
                  
                </div>
              )}
              </>
              )}

              {/* STOCK INICIAL - Común a todas las categorías */}
              <div className="form-group">
                <label>STOCK INICIAL *</label>
                <input
                  type="number"
                  min="1"
                  value={nuevoProducto.stock}
                  onChange={(e) => setNuevoProducto({
                    ...nuevoProducto,
                    stock: parseInt(e.target.value) || 0
                  })}
                  disabled={creando}
                  placeholder="Cantidad inicial"
                />
              </div>

              {/* Campo de sucursal solo visible para admin */}
              {user?.rol === 'admin' && (
                <div className="form-group">
                  <label>SUCURSAL *</label>
                  <select
                    value={nuevoProducto.sucursal}
                    onChange={(e) => setNuevoProducto({
                      ...nuevoProducto,
                      sucursal: e.target.value
                    })}
                    disabled={creando}
                  >
                    <option value="">Seleccione una sucursal</option>
                    <option value="parana">Paraná</option>
                    <option value="rosario">Rosario</option>
                    <option value="esperanza">Esperanza</option>
                  </select>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button 
                className="btn-secondary" 
                onClick={cerrarModal}
                disabled={creando}
              >
                Cancelar
              </button>
              <button 
                className="btn-primary" 
                onClick={handleCrearProducto}
                disabled={creando}
              >
                {creando ? 'Creando...' : 'Crear Producto'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de editar producto */}
      {modalEditar && productoEditando && (
        <div className="modal-overlay" onClick={() => setModalEditar(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Editar Producto</h2>
              <button className="modal-close" onClick={() => setModalEditar(false)}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="producto-info">
                <h3>{productoEditando.tipo} {productoEditando.modelo}</h3>
                <p><strong>Categoría:</strong> {productoEditando.categoria}</p>
                <p><strong>Medidas:</strong> {productoEditando.varianteSeleccionada?.medidas}</p>
                <p><strong>Madera:</strong> {productoEditando.varianteSeleccionada?.madera}</p>
                <p><strong>Lustre:</strong> {productoEditando.varianteSeleccionada?.lustre || '-'}</p>
                <p><strong>Sucursal:</strong> {sucursalEditar?.toUpperCase()}</p>
              </div>

              <div className="form-group">
                <label>STOCK *</label>
                <input
                  type="number"
                  min="0"
                  value={stockEditar}
                  onChange={(e) => setStockEditar(parseInt(e.target.value) || 0)}
                  disabled={creando}
                  placeholder="Cantidad en stock"
                />
              </div>
            </div>

            <div className="modal-footer">
              <button 
                className="btn-secondary" 
                onClick={() => setModalEditar(false)}
                disabled={creando}
              >
                Cancelar
              </button>
              <button 
                className="btn-primary" 
                onClick={handleGuardarEdicion}
                disabled={creando}
              >
                {creando ? 'Guardando...' : 'Guardar Cambios'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Productos;